import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Radio,
  RadioGroup,
  IconButton,
  InputAdornment,
  Checkbox,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import {
  LocationOn,
  Payment,
  Info as InfoIcon,
  CheckCircle,
  Cancel,
  Block,
  Map as MapIcon,
  Save,
  Visibility,
  VisibilityOff,
  Build,
  PauseCircle,
  CalendarMonth,
  EventNote,
  Notes,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import { Contract, Client } from '../../types';
import { formatarData } from '../../utils/formatToView';
import { contractService } from '../../services/contractService';
import { clientService } from '../../services/clientService';
import templateService, { Template } from '../../services/templateService';
import SelectEstadosBr from '../selects/EstadosBrSelect';
import GatewaySelect from '../selects/GatewaySelect';
import EscritorioSelect from '../selects/EscritorioSelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import PlanosSelect from '../selects/PlanosSelect';
import NasSelect from '../selects/NasSelect';
import DiasVencimentoSelect from '../selects/DiasVencimentoSelect';
import CfopSelect from '../selects/CfopSelect';
import { getStatusColor, getStatusLabel, getStatusIcon } from '../../utils/contractStatusUtils';
import { usePermissions } from '../../contexts/AuthContext';

const getMotivoLabel = (motivo?: string) => {
  switch (motivo) {
    case 'inadimplencia':
      return 'Inadimplência';
    case 'ferias':
      return 'Férias';
    case 'suspensao_temporaria':
      return 'Suspensão Temporária';
    default:
      return motivo || 'Não informado';
  }
};

// Helpers de formatação (alinhados ao TabbedContractForm)
const formatCurrencyInput = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (!numericValue) return '';
  const cents = parseInt(numericValue);
  const reais = cents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(reais);
};

const parseCurrencyInput = (formattedValue: string): number => {
  const numericString = formattedValue
    .replace(/[R$\s.]/g, '')
    .replace(',', '.');
  return parseFloat(numericString) || 0;
};

const formatCEP = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (numericValue.length <= 5) return numericValue;
  return `${numericValue.slice(0, 5)}-${numericValue.slice(5, 8)}`;
};

const parseCEP = (value: string): string => value.replace(/\D/g, '');

interface ContractOverviewFormData extends Partial<Contract> {
  usar_padrao_corte?: boolean;
  gateway_id?: number | null;
}

const contractEditSchema = yup.object().shape({
  titulo: yup.string().required('Título é obrigatório').max(255, 'Máximo 255 caracteres'),
  escritorio_id: yup.number().required('Escritório é obrigatório'),
  data_contrato: yup.string().required('Data do contrato é obrigatória'),
  data_instalacao: yup.string().required('Data de instalação é obrigatória'),
  dia_vencimento: yup.number().required('Dia de vencimento é obrigatório').min(1).max(31),
  fidelidade: yup.number().required('Fidelidade é obrigatória').min(0),
  usar_padrao_corte: yup.boolean().optional(),
  dias_corte: yup
    .number()
    .nullable()
    .when('usar_padrao_corte', {
      is: true,
      then: (s) => s.nullable().notRequired(),
      otherwise: (s) => s.required('Dias para corte é obrigatório').min(0),
    }),
  tx_instalacao: yup.number().required('Taxa de instalação é obrigatória').min(0),
  valor_contratado: yup.number().required('Valor do contrato é obrigatório').min(0),
  acrescimo: yup.number().required('Acréscimo é obrigatório').min(0),
  desconto: yup.number().required('Desconto é obrigatório').min(0),
  cep_inst: yup.string().required('CEP de instalação é obrigatório'),
  cidade_inst: yup.string().required('Cidade de instalação é obrigatória'),
  estado_inst: yup.string().required('Estado de instalação é obrigatório').length(2),
  bairro_inst: yup.string().required('Bairro de instalação é obrigatório'),
  endereco_inst: yup.string().required('Endereço de instalação é obrigatório'),
  modalidade: yup.string().oneOf(['pre-pago', 'pos-pago']).optional(),
  tipo_moradia: yup.string().oneOf(['propria', 'alugado']).optional().nullable(),
  gateway_id: yup.number().nullable().optional(),
});

interface ContractOverviewProps {
  contract: Contract;
  onRefresh: () => void;
}

const ContractOverview: React.FC<ContractOverviewProps> = ({ contract, onRefresh }) => {
  const theme = useTheme();
  const { can } = usePermissions();
  const podeEditar = can('contratos.editar');
  const [saving, setSaving] = useState(false);
  const [contratoTemplates, setContratoTemplates] = useState<Template[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<ContractOverviewFormData>({
    resolver: yupResolver(contractEditSchema as any) as any,
    defaultValues: getDefaultValues(contract),
  });

  function getDefaultValues(c: Contract): ContractOverviewFormData {
    return {
      titulo: c.titulo || '',
      escritorio_id: c.escritorio_id,
      status: c.status || 'ativo',
      plano_id: c.plano_id,
      data_contrato: c.data_contrato ? c.data_contrato.split('T')[0] : '',
      data_instalacao: c.data_instalacao ? c.data_instalacao.split('T')[0] : '',
      dia_vencimento: c.dia_vencimento ?? 10,
      fidelidade: c.fidelidade ?? 12,
      isento: c.isento ?? false,
      sms_lembrete: c.sms_lembrete ?? false,
      gerar_fatura_automatico: c.gerar_fatura_automatico ?? false,
      geranfcom: c.geranfcom ?? false,
      pgcorte: c.pgcorte ?? true,
      dias_corte: c.dias_corte ?? undefined,
      usar_padrao_corte: c.dias_corte == null,
      tx_instalacao: c.tx_instalacao ?? 0,
      valor_contratado: c.valor_contratado ?? undefined,
      acrescimo: c.acrescimo ?? 0,
      desconto: c.desconto ?? 0,
      cep_inst: c.cep_inst || '',
      cidade_inst: c.cidade_inst || '',
      estado_inst: c.estado_inst || '',
      bairro_inst: c.bairro_inst || '',
      endereco_inst: c.endereco_inst || '',
      numero_inst: c.numero_inst || '',
      complemento_inst: c.complemento_inst || '',
      codigo_municipio: c.codigo_municipio || '',
      referencia_inst: c.referencia_inst || '',
      nas_server: c.nas_server ?? null,
      usuario_pppoe: c.usuario_pppoe || '',
      senha_pppoe: c.senha_pppoe || '',
      gateway_id: c.gateway_id ?? null,
      cfop: c.cfop,
      modalidade: (c.modalidade as 'pos-pago' | 'pre-pago') || 'pos-pago',
      tipo_moradia: c.tipo_moradia ?? undefined,
      contrato_template: c.contrato_template ?? undefined,
      vendedor_id: c.vendedor_id ?? undefined,
      observacoes: c.observacoes ?? '',
    };
  }

  useEffect(() => {
    reset(getDefaultValues(contract));
  }, [contract.id, contract.updatedAt, reset]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await templateService.getTemplates({
          type: 'contrato',
          isActive: true,
          limit: 100,
        });
        setContratoTemplates(response.templates || []);
      } catch (err) {
        console.error('Erro ao carregar templates de contrato:', err);
        setContratoTemplates([]);
      }
    };
    loadTemplates();
  }, []);

  const handleSave = useCallback(
    async (data: ContractOverviewFormData) => {
      if (!podeEditar) return;
      const changedKeys = Object.keys(dirtyFields) as (keyof ContractOverviewFormData)[];

      if (changedKeys.length === 0) {
        toast.info('Nenhum campo foi alterado.');
        return;
      }

      const payload: Record<string, any> = { cliente_id: contract.cliente_id };
      const usarPadraoCorte = data.usar_padrao_corte;

      for (const key of changedKeys) {
        if (key === 'usar_padrao_corte') continue;

        if (key === 'dias_corte') {
          payload.dias_corte = usarPadraoCorte ? null : (data.dias_corte ?? null);
        } else if (key === 'contrato_template') {
          payload.contrato_template = data.contrato_template ?? null;
        } else if (key === 'gateway_id') {
          payload.gateway_id = data.gateway_id ? Number(data.gateway_id) : null;
        } else {
          payload[key] = data[key];
        }
      }

      if (dirtyFields.usar_padrao_corte && !dirtyFields.dias_corte) {
        payload.dias_corte = usarPadraoCorte ? null : (data.dias_corte ?? null);
      }

      setSaving(true);
      try {
        const response = await contractService.update(contract.id, payload as any);
        if (response.success) {
          toast.success('Contrato atualizado com sucesso.');
          onRefresh();
        } else {
          const msg = response.message || 'Erro ao atualizar contrato';
          if (response.errors?.length) {
            response.errors.forEach((err: any) => toast.error(err.message || err));
          } else {
            toast.error(msg);
          }
        }
      } catch (err: any) {
        const msg = err.response?.data?.message || err.message || 'Erro ao atualizar contrato';
        toast.error(msg);
      } finally {
        setSaving(false);
      }
    },
    [contract.id, contract.cliente_id, onRefresh, dirtyFields, podeEditar]
  );

  const watchedEscritorioId = watch('escritorio_id');

  return (
    <Box component="form" onSubmit={handleSubmit(handleSave)}>
      <Box
        component="fieldset"
        disabled={!podeEditar}
        sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
      >
        <Grid container spacing={3}>
          {/* Seção: Informações Básicas */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <InfoIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Informações Básicas
                  </Typography>
                  <Chip
                    size="small"
                    label={getStatusLabel(contract.status)}
                    color={getStatusColor(contract.status) as any}
                    icon={getStatusIcon(contract.status, 'small')}
                  />
                </Box>

                {/* Alertas de Status (Cancelado / Bloqueado / Confiança / Suspensão) */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {contract.status === 'cancelado' && (
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: alpha(theme.palette.error.main, 0.05), p: 2, borderRadius: 2, borderLeft: `4px solid ${theme.palette.error.main}` }}>
                        <Typography variant="subtitle2" color="error.dark" fontWeight="bold">CONTRATO CANCELADO</Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">Cancelado em</Typography>
                            <Typography variant="body2" fontWeight="bold">{contract.data_cancelamento ? formatarData(contract.data_cancelamento) : '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">Cancelado por</Typography>
                            <Typography variant="body2" fontWeight="bold">{contract.cancelado_por || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">Motivo</Typography>
                            <Typography variant="body2" fontWeight="bold">{contract.motivo_cancelamento || 'Não informado'}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                  {contract.status === 'suspenso' && (
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: 1,
                          borderColor: alpha(theme.palette.warning.main, 0.3),
                          background: theme.palette.mode === 'dark'
                            ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${alpha(theme.palette.warning.dark, 0.04)} 100%)`
                            : `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
                          boxShadow: `0 2px 12px 0 ${alpha(theme.palette.warning.main, 0.06)}`,
                        }}
                      >
                        {/* Header Compacto */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.warning.main, 0.15),
                                color: 'warning.main',
                              }}
                            >
                              <PauseCircle sx={{ fontSize: 20 }} />
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="700" sx={{ color: 'warning.dark', lineHeight: 1.1 }}>
                                Contrato Suspenso
                              </Typography>
                            </Box>
                          </Box>

                          <Chip
                            label={getMotivoLabel(contract.suspensao_motivo)}
                            size="small"
                            color="warning"
                            variant="filled"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 1,
                              height: 22,
                              fontSize: '0.72rem',
                            }}
                          />
                        </Box>

                        {/* Grid de Cards Compactos */}
                        <Grid container spacing={1.25}>
                          <Grid item xs={12} sm={4}>
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: theme.palette.background.paper,
                                border: 1,
                                borderColor: alpha(theme.palette.divider, 0.5),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <CalendarMonth color="warning" sx={{ fontSize: 19, opacity: 0.8 }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1 }}>
                                  Início
                                </Typography>
                                <Typography variant="body2" fontWeight="700" sx={{ mt: 0.25, fontSize: '0.8125rem', color: 'text.primary' }}>
                                  {contract.suspensao_inicio ? formatarData(contract.suspensao_inicio) : '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: theme.palette.background.paper,
                                border: 1,
                                borderColor: alpha(theme.palette.divider, 0.5),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <EventNote color="warning" sx={{ fontSize: 19, opacity: 0.8 }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1 }}>
                                  Fim da Suspensão
                                </Typography>
                                <Typography variant="body2" fontWeight="700" sx={{ mt: 0.25, fontSize: '0.8125rem', color: 'text.primary' }}>
                                  {contract.suspensao_fim ? formatarData(contract.suspensao_fim) : 'Indeterminado'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: theme.palette.background.paper,
                                border: 1,
                                borderColor: alpha(theme.palette.divider, 0.5),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <InfoIcon color="warning" sx={{ fontSize: 19, opacity: 0.8 }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1 }}>
                                  Motivo
                                </Typography>
                                <Typography variant="body2" fontWeight="700" sx={{ mt: 0.25, fontSize: '0.8125rem', color: 'text.primary' }} noWrap>
                                  {getMotivoLabel(contract.suspensao_motivo)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          {/* Card Compacto da Observação */}
                          {contract.suspensao_obs && (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  px: 1.25,
                                  py: 1,
                                  mt: 0.25,
                                  borderRadius: 1.5,
                                  bgcolor: theme.palette.background.paper,
                                  border: 1,
                                  borderColor: alpha(theme.palette.warning.main, 0.25),
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                  <Notes color="warning" sx={{ fontSize: 15 }} />
                                  <Typography variant="caption" fontWeight="700" color="warning.dark" sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.3 }}>
                                    Observação da Suspensão
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.primary',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.4,
                                    fontSize: '0.8125rem',
                                  }}
                                >
                                  {contract.suspensao_obs}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  {contract.status === 'bloqueado' && contract.data_bloqueio && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05), p: 1.5, borderRadius: 2, borderLeft: `4px solid ${theme.palette.warning.main}` }}>
                        <Typography variant="caption" color="text.secondary">Contrato Bloqueado em</Typography>
                        <Typography variant="body1" color="error.main" fontWeight="bold">
                          {formatarData(contract.data_bloqueio)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {contract.desbloqueio_confianca && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ bgcolor: alpha(theme.palette.success.main, 0.05), p: 1.5, borderRadius: 2, borderLeft: `4px solid ${theme.palette.success.main}` }}>
                        <Typography variant="caption" color="text.secondary">Desbloqueio em Confiança</Typography>
                        <Typography variant="body1" color="success.main" fontWeight="bold">
                          Não bloquear até: {formatarData(contract.desbloqueio_confianca)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="escritorio_id"
                      control={control}
                      render={({ field }) => (
                        <EscritorioSelect
                          value={field.value ?? null}
                          onChange={(v) => field.onChange(v)}
                          label="Escritório"
                          size="small"
                          required
                          allowClear={false}
                          multiple={false}
                          onlyActive
                          error={!!errors.escritorio_id}
                          helperText={errors.escritorio_id?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Controller
                      name="titulo"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Título do Contrato"
                          size="small"
                          error={!!errors.titulo}
                          helperText={errors.titulo?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="data_contrato"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Data do Contrato"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          error={!!errors.data_contrato}
                          helperText={errors.data_contrato?.message}
                          disabled={true}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="data_instalacao"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Data de Instalação"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          error={!!errors.data_instalacao}
                          helperText={errors.data_instalacao?.message}
                          disabled={true}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="fidelidade"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Fidelidade (meses)"
                          type="number"
                          inputProps={{ min: 0 }}
                          size="small"
                          error={!!errors.fidelidade}
                          helperText={errors.fidelidade?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="dias_corte"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Dias para Corte"
                          type="number"
                          inputProps={{ min: 0 }}
                          size="small"
                          disabled={watch('usar_padrao_corte')}
                          error={!!errors.dias_corte}
                          helperText={errors.dias_corte?.message}
                        />
                      )}
                    />
                    <Controller
                      name="usar_padrao_corte"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={!!field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                if (e.target.checked) setValue('dias_corte', undefined as any);
                              }}
                            />
                          }
                          label="Padrão do sistema"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Controller
                      name="pgcorte"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={!!field.value} />}
                          label="Corte automático (pgcorte)"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="contrato_template"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small">
                          <InputLabel shrink>Modelo de Contrato</InputLabel>
                          <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                            label="Modelo de Contrato"
                            displayEmpty
                            renderValue={(v) => {
                              if (!v) return 'Nenhum';
                              const t = contratoTemplates.find((x) => x.id === v);
                              return t ? t.title : 'Nenhum';
                            }}
                          >
                            <MenuItem value="">
                              <em>Nenhum</em>
                            </MenuItem>
                            {/* Evita o warning do MUI de valor fora do range durante o loading */}
                            {field.value && !contratoTemplates.some(t => t.id === field.value) && (
                              <MenuItem value={field.value} sx={{ display: 'none' }}>
                                {field.value}
                              </MenuItem>
                            )}
                            {contratoTemplates.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.title}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Modelo padrão para impressão do contrato
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="vendedor_id"
                      control={control}
                      render={({ field }) => (
                        <FuncionariosSelect
                          value={field.value ?? null}
                          onChange={(id) => field.onChange(id)}
                          label="Vendedor"
                          size="small"
                          placeholder="Selecione o vendedor"
                          allowClear={true}
                          disabled={true}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Endereço */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Endereço de Instalação
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="cep_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          fullWidth
                          label="CEP"
                          value={field.value ? formatCEP(field.value) : ''}
                          onChange={(e) => {
                            const formatted = formatCEP(e.target.value);
                            field.onChange(parseCEP(formatted));
                          }}
                          placeholder="00000-000"
                          inputProps={{ maxLength: 9 }}
                          size="small"
                          error={!!errors.cep_inst}
                          helperText={errors.cep_inst?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Controller
                      name="cidade_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Cidade"
                          size="small"
                          error={!!errors.cidade_inst}
                          helperText={errors.cidade_inst?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="estado_inst"
                      control={control}
                      render={({ field }) => (
                        <SelectEstadosBr
                          disabled={true}
                          value={field.value || ''}
                          onChange={field.onChange}
                          label="Estado"
                          error={!!errors.estado_inst}
                          helperText={errors.estado_inst?.message}
                          required
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="codigo_municipio"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Código do Município"
                          size="small"
                          error={!!errors.codigo_municipio}
                          helperText={errors.codigo_municipio?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="bairro_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Bairro"
                          size="small"
                          error={!!errors.bairro_inst}
                          helperText={errors.bairro_inst?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="endereco_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Endereço"
                          size="small"
                          error={!!errors.endereco_inst}
                          helperText={errors.endereco_inst?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="numero_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Número"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Controller
                      name="referencia_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Referência"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="complemento_inst"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          disabled={true}
                          {...field}
                          fullWidth
                          label="Complemento"
                          size="small"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Controller
                      name="tipo_moradia"
                      control={control}
                      render={({ field }) => (
                        <FormControl component="fieldset" size="small" error={!!errors.tipo_moradia}>
                          <RadioGroup
                            row
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value || undefined)}
                          >
                            <FormControlLabel disabled={true} value="propria" control={<Radio size="small" />} label="Própria" />
                            <FormControlLabel disabled={true} value="alugado" control={<Radio size="small" />} label="Alugado" />
                          </RadioGroup>
                          {errors.tipo_moradia && (
                            <FormHelperText>{errors.tipo_moradia.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Financeiro */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Payment color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Financeiro
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="valor_contratado"
                      control={control}
                      render={({ field }) => {
                        const display =
                          field.value == null || field.value === undefined
                            ? ''
                            : new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(field.value);
                        return (
                          <TextField
                            fullWidth
                            label="Valor do Contrato"
                            value={display}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (!v || v.trim() === '') {
                                field.onChange(undefined);
                                return;
                              }
                              field.onChange(parseCurrencyInput(formatCurrencyInput(v)));
                            }}
                            placeholder="R$ 0,00"
                            size="small"
                            inputProps={{ inputMode: 'numeric' }}
                            error={!!errors.valor_contratado}
                            helperText={errors.valor_contratado?.message}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="tx_instalacao"
                      control={control}
                      render={({ field }) => {
                        const display =
                          !field.value && field.value !== 0
                            ? ''
                            : new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(field.value);
                        return (
                          <TextField
                            fullWidth
                            label="Taxa de Instalação"
                            value={display}
                            onChange={(e) =>
                              field.onChange(parseCurrencyInput(formatCurrencyInput(e.target.value)))
                            }
                            placeholder="R$ 0,00"
                            size="small"
                            inputProps={{ inputMode: 'numeric' }}
                            error={!!errors.tx_instalacao}
                            helperText={errors.tx_instalacao?.message}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="acrescimo"
                      control={control}
                      render={({ field }) => {
                        const display =
                          !field.value && field.value !== 0
                            ? ''
                            : new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(field.value);
                        return (
                          <TextField
                            fullWidth
                            label="Acréscimo"
                            value={display}
                            onChange={(e) =>
                              field.onChange(parseCurrencyInput(formatCurrencyInput(e.target.value)))
                            }
                            placeholder="R$ 0,00"
                            size="small"
                            inputProps={{ inputMode: 'numeric' }}
                            error={!!errors.acrescimo}
                            helperText={errors.acrescimo?.message}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="desconto"
                      control={control}
                      render={({ field }) => {
                        const display =
                          !field.value && field.value !== 0
                            ? ''
                            : new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(field.value);
                        return (
                          <TextField
                            fullWidth
                            label="Desconto"
                            value={display}
                            onChange={(e) =>
                              field.onChange(parseCurrencyInput(formatCurrencyInput(e.target.value)))
                            }
                            placeholder="R$ 0,00"
                            size="small"
                            inputProps={{ inputMode: 'numeric' }}
                            error={!!errors.desconto}
                            helperText={errors.desconto?.message}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="dia_vencimento"
                      control={control}
                      render={({ field }) => (
                        <DiasVencimentoSelect
                          value={field.value}
                          onChange={(v) => field.onChange(v === '' ? undefined : v)}
                          error={!!errors.dia_vencimento}
                          helperText={errors.dia_vencimento?.message}
                          required
                          disabled={true}
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="modalidade"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" error={!!errors.modalidade}>
                          <InputLabel>Modalidade</InputLabel>
                          <Select {...field} label="Modalidade" value={field.value || 'pos-pago'}>
                            <MenuItem value="pos-pago">
                              Pós-pago{' '}
                              <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                                - Paga depois de usar
                              </Box>
                            </MenuItem>
                            <MenuItem value="pre-pago">
                              Pré-pago{' '}
                              <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                                - Paga antes de usar
                              </Box>
                            </MenuItem>
                          </Select>
                          {errors.modalidade && (
                            <FormHelperText>{errors.modalidade.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="gateway_id"
                      control={control}
                      render={({ field }) => (
                        <GatewaySelect
                          value={field.value || ''}
                          onChange={(id) => field.onChange(id ?? null)}
                          label="Gateway de Pagamento"
                          showOnlyActive
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="cfop"
                      control={control}
                      render={({ field }) => (
                        <CfopSelect
                          value={field.value || undefined}
                          onChange={(v) => field.onChange(v)}
                          error={!!errors.cfop}
                          helperText={errors.cfop?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="isento"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={!!field.value} />}
                          label="Isento de Mensalidades"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="gerar_fatura_automatico"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={!!field.value} />}
                          label="Gerar Fatura Automático"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="geranfcom"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={!!field.value} />}
                          label="Gerar NFCom"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="sms_lembrete"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={!!field.value} />}
                          label="SMS Lembrete"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Observacao */}
          <Grid item xs={12}>
            <Controller
              name="observacoes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observações"
                  multiline
                  rows={8}
                  fullWidth
                  size="small"
                  error={!!errors.observacoes}
                  helperText={errors.observacoes?.message}
                  sx={{
                    "& textarea": {
                      resize: "vertical"
                    }
                  }}
                />
              )}
            />
          </Grid>

          {podeEditar && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  disabled={!isDirty || saving}
                >
                  {saving ? 'Salvando...' : 'Salvar alterações'}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default ContractOverview;
